import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { Box, alpha, useTheme, type Theme } from '@mui/material';
import { defaultTimeConverter } from 'mq-timeline-calendar';
import type { TimelineEngine } from 'mq-timeline-calendar/core';
import {
  CalendarContent,
  CalendarHeader,
  TimelineContext,
  themes,
  useResize,
  useTimelineEngine,
  useTouch,
  useWheel,
  type CalendarLocale,
  type HeaderCellRenderParams,
  type SidebarConfig,
  type TimelineTheme,
} from 'mq-timeline-calendar/react';
import {
  buildTenantGrid,
  describeHeaderCell,
  viewportCorrection,
} from './supervisorTaskCalendar';

/** Used until the container has been measured, like the widget's own default. */
const FALLBACK_WIDTH = 1000;
/** Pinch deltas arrive as percent change. */
const MIN_PINCH_FACTOR = 0.5;
const MAX_PINCH_FACTOR = 2;
/** Opacity of the warning tint behind a repeated (autumn) hour. */
const FOLD_TINT_ALPHA = 0.18;

const TIME_UNITS = [
  'century',
  'decade',
  'year',
  'month',
  'week',
  'day',
  'halfday',
  'quarterday',
  'hour',
  'halfhour',
  'quarterhour',
  'minute',
  'halfminute',
  'quarterminute',
  'second',
  'millisecond',
] as const;

/**
 * The CSS variables the widget's rows, items and header read from their root. A colour the
 * widget theme leaves out falls back to the app's MUI palette, which follows light/dark.
 */
function themeVariables(theme: TimelineTheme, palette: Theme['palette']): CSSProperties {
  const { colors, fonts, spacing } = theme;
  const vars: Record<string, string> = {
    '--timeline-bg': colors?.background ?? palette.background.paper,
    '--timeline-grid-line': colors?.gridLine ?? palette.divider,
    '--timeline-grid-line-primary': colors?.gridLinePrimary ?? palette.text.disabled,
    '--timeline-header-bg': colors?.headerBackground ?? palette.background.default,
    '--timeline-header-text': colors?.headerText ?? palette.text.primary,
    '--timeline-header-border': colors?.headerBorder ?? palette.divider,
    '--timeline-current-time-line': colors?.currentTimeLine ?? palette.error.main,
    '--timeline-header-font': fonts?.header ?? 'system-ui, sans-serif',
    '--timeline-content-font': fonts?.content ?? 'system-ui, sans-serif',
    '--timeline-header-height': `${String(spacing?.headerHeight ?? 80)}px`,
    '--timeline-header-row-height': `${String(spacing?.headerRowHeight ?? 40)}px`,
    '--timeline-row-height': `${String(spacing?.rowHeight ?? 60)}px`,
    '--timeline-row-header-height': `${String(spacing?.rowHeaderHeight ?? 40)}px`,
  };
  for (const unit of TIME_UNITS) {
    const type = colors?.timeTypes?.[unit];
    if (type?.text) vars[`--timeline-${unit}-text`] = type.text;
    if (type?.line) vars[`--timeline-${unit}-line`] = type.line;
  }
  return vars as CSSProperties;
}

export interface SupervisorTaskCalendarGridProps {
  /** The fetched window: the tenant's first midnight up to the next one after the last day. */
  startMs: number;
  endMs: number;
  timeZone: string;
  /** The widget's own locale for the UI language (weekday names). */
  locale: CalendarLocale;
  /** UI language (BCP 47), for the long date in a cell's tooltip. */
  language: string;
  height: number | string;
  theme: 'light' | 'dark';
  sidebar?: SidebarConfig | undefined;
  /** Rows and items of the widget; they read the engine from context. */
  children?: ReactNode;
}

/**
 * The timeline for the supervisor task calendar, assembled from the widget's own
 * headless parts (engine, header, content area, rows and items) with two changes:
 *
 * - the header cells and grid lines come from `buildTenantGrid`, so days start at
 *   the tenant's midnight and both repeated autumn hours have a cell, whatever
 *   zone the browser is in;
 * - panning and zooming stay inside the fetched window, so the grid never shows
 *   time whose tasks were not loaded.
 *
 * Everything else (bars, rows, drag, the sidebar) is the widget's.
 */
export function SupervisorTaskCalendarGrid({
  startMs,
  endMs,
  timeZone,
  locale,
  language,
  height,
  theme,
  sidebar,
  children,
}: SupervisorTaskCalendarGridProps) {
  const muiTheme = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  // The widget's hooks take a ref to a mounted element; they cope with the pre-mount null.
  const rootElementRef = rootRef as RefObject<HTMLElement>;
  const { width: rootWidth } = useResize(rootElementRef);
  const sidebarWidth = sidebar ? sidebar.width + 1 : 0;
  const contentWidth = Math.max(0, rootWidth - sidebarWidth);

  const startDate = useMemo(() => new Date(startMs), [startMs]);
  const endDate = useMemo(() => new Date(endMs), [endMs]);
  const { engine, refresh, refreshCounter } = useTimelineEngine({
    startDate,
    endDate,
    containerWidth: contentWidth || FALLBACK_WIDTH,
    locale,
    animateDateChanges: false,
  });

  const keepInWindow = useCallback(
    (target: TimelineEngine) => {
      const { start, end } = target.getViewportState();
      const correction = viewportCorrection(start, end, startMs, endMs);
      if (!correction) return;
      if (correction.kind === 'fit') target.zoomToRange(startMs, endMs);
      else target.scroll(correction.deltaMs * target.getZoomState().pixelsPerMs);
    },
    [startMs, endMs],
  );

  const scrollBy = useCallback(
    (deltaPx: number) => {
      if (!engine) return;
      engine.cancelAnimation();
      engine.scroll(deltaPx);
      keepInWindow(engine);
      refresh();
    },
    [engine, keepInWindow, refresh],
  );

  const zoomBy = useCallback(
    (factor: number, focalX: number) => {
      if (!engine) return;
      engine.zoom(factor, Math.max(0, focalX - sidebarWidth));
      keepInWindow(engine);
      refresh();
    },
    [engine, keepInWindow, refresh, sidebarWidth],
  );

  const wheelOptions = useMemo(
    () => ({
      onWheel: (deltaX: number) => { scrollBy(deltaX); },
      onZoom: (factor: number, clientX: number) => { zoomBy(factor, clientX); },
    }),
    [scrollBy, zoomBy],
  );
  useWheel(rootElementRef, wheelOptions);
  useTouch(rootElementRef, {
    onScroll: scrollBy,
    onZoom: (percent, centerX) => {
      zoomBy(
        Math.min(MAX_PINCH_FACTOR, Math.max(MIN_PINCH_FACTOR, 1 + percent / 100)),
        centerX,
      );
    },
  });

  // The sidebar scrolls with the rows.
  const hasSidebar = sidebar !== undefined;
  useEffect(() => {
    if (!hasSidebar) return undefined;
    const content = rootRef.current?.querySelector<HTMLElement>('[data-timeline-content]');
    const side = sidebarRef.current;
    if (!content || !side) return undefined;
    const sync = () => { side.scrollTop = content.scrollTop; };
    content.addEventListener('scroll', sync);
    return () => { content.removeEventListener('scroll', sync); };
  }, [hasSidebar]);

  // Read after every refresh: the engine is mutated in place, `refreshCounter` marks the change.
  const grid = useMemo(() => {
    if (!engine) return { headerCells: [], gridLines: [], stepMinutes: null };
    const viewport = engine.getViewportState();
    return buildTenantGrid({
      viewStartMs: viewport.start,
      viewEndMs: viewport.end,
      pixelsPerMs: engine.getZoomState().pixelsPerMs,
      windowStartMs: startMs,
      windowEndMs: endMs,
      timeZone,
      locale,
      language,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `refreshCounter` is the engine's change signal.
  }, [engine, refreshCounter, startMs, endMs, timeZone, locale, language]);

  const renderHeaderCell = useCallback(
    (cell: HeaderCellRenderParams): ReactNode => {
      const described = describeHeaderCell(cell, locale, timeZone, language);
      if (!described) return null;
      return (
        <Box
          component="div"
          title={described.title}
          data-testid="supervisor-calendar-header-cell"
          data-cell-type={cell.type}
          data-tenant-date={described.date}
          data-offset-shown={String(described.offsetShown)}
          data-day-hours={described.hours === null ? undefined : String(described.hours)}
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            px: 1,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            // A repeated hour is two lines ("03" / "(+3)") so it fits its one-hour cell.
            whiteSpace: described.offsetShown ? 'normal' : 'nowrap',
            lineHeight: described.offsetShown ? 1.1 : undefined,
            borderRight: '1px solid var(--timeline-header-border)',
            color: `var(--timeline-${cell.type}-text, var(--timeline-header-text))`,
            fontSize: cell.isPrimary ? 14 : described.offsetShown ? 10 : 12,
            fontWeight: 600,
            opacity: 0.85,
            ...(described.offsetShown && {
              bgcolor: alpha(muiTheme.palette.warning.main, FOLD_TINT_ALPHA),
            }),
          }}
        >
          {described.text}
        </Box>
      );
    },
    [locale, timeZone, language, muiTheme.palette.warning.main],
  );

  const context = useMemo(
    () => ({ engine, refresh, timeConverter: defaultTimeConverter, refreshCounter }),
    [engine, refresh, refreshCounter],
  );
  const cssVariables = useMemo(
    () => themeVariables(themes[theme], muiTheme.palette),
    [theme, muiTheme.palette],
  );

  const header = (
    <CalendarHeader headerCells={grid.headerCells} renderHeaderCell={renderHeaderCell} />
  );
  const content = <CalendarContent gridLines={grid.gridLines}>{children}</CalendarContent>;

  return (
    <TimelineContext.Provider value={context}>
      <div
        ref={rootRef}
        data-timeline-calendar
        data-testid="supervisor-calendar-grid"
        data-clock-step={grid.stepMinutes === null ? undefined : String(grid.stepMinutes)}
        style={{
          width: '100%',
          height,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          ...cssVariables,
        }}
      >
        {sidebar ? (
          <>
            <div style={{ display: 'flex', flexShrink: 0 }}>
              <div
                style={{
                  width: sidebar.width,
                  flexShrink: 0,
                  height: 'var(--timeline-header-height)',
                  borderRight: '1px solid var(--timeline-header-border)',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {sidebar.headerContent}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>{header}</div>
            </div>
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              <div
                ref={sidebarRef}
                style={{
                  width: sidebar.width,
                  flexShrink: 0,
                  overflowY: 'hidden',
                  borderRight: '1px solid var(--timeline-header-border)',
                  boxSizing: 'border-box',
                }}
              >
                {sidebar.content}
              </div>
              <div
                style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}
              >
                {content}
              </div>
            </div>
          </>
        ) : (
          <>
            {header}
            {content}
          </>
        )}
      </div>
    </TimelineContext.Provider>
  );
}
